<template>
    <div>
        <Toast />
        <ConfirmDialog></ConfirmDialog>

        <div class="card">
            <DataTable :value="lstFacturaciones" :loading="loading" responsiveLayout="scroll">
                <template #header>
                    <div class="flex justify-content-between align-items-center">
                        <h5 class="m-0">Catálogo de Facturación</h5>
                        <Button 
                            label="Nuevo"
                            class="p-button-success" 
                            @click="abrirDialogoNuevo" />
                    </div>
                </template>

                <template #empty>No se encontraron registros.</template>

                <Column field="Anio" header="Año" sortable></Column>
                
                <Column field="Mes" header="Mes" sortable> 
                    <template #body="slotProps">
                        {{ obtenerNombreMes(slotProps.data.Mes) }}
                    </template>
                </Column>
                
                <Column field="Cuenta" header="Cuenta" sortable></Column>
                
                <Column field="Estatus" header="Estatus" sortable 
                        headerStyle="text-align: center;" 
                        bodyStyle="text-align: center;">
                    <template #body="slotProps">
                        <div style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
                            <InputSwitch 
                                v-model="slotProps.data.estatusBooleano" 
                                @update:modelValue="cambiarEstatus(slotProps.data)" 
                            />
                            <span :class="[slotProps.data.Estatus === 'Pagado' ? 'estatus-pagado' : 'estatus-pendiente']">
                                {{ slotProps.data.Estatus }}
                            </span>
                        </div>
                    </template>
                </Column>

                <Column field="Monto" header="Monto" sortable>
                    <template #body="slotProps">
                        {{ formatCurrency(slotProps.data.Monto) }}
                    </template>
                </Column>

                <Column header="PDF" :exportable="false" bodyStyle="text-align: center;" headerStyle="text-align: center;" style="width: 80px;">
                    <template #body="slotProps">
                        <Button 
                            v-if="slotProps.data.Ruta_Archivo_PDF"
                            icon="pi pi-download" 
                            class="p-button-rounded p-button-success p-button-text" 
                            @click="descargarPDF(slotProps.data)"
                            v-tooltip.top="'Descargar PDF'" />
                        <span v-else class="text-muted">Sin PDF</span>
                    </template>
                </Column>

                <Column header="Acciones" :exportable="false" bodyStyle="text-align: center;" headerStyle="text-align: center; min-width: 8rem;">
                    <template #body="slotProps">
                        <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editarFactura(slotProps.data)" />
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="confirmarEliminar(slotProps.data)" />
                    </template>
                </Column>
            </DataTable>
        </div>

        <Dialog v-model:visible="mostrarDialogo" :style="{width: '450px'}" header="Detalles de Factura" :modal="true" class="p-fluid">
            
            <div class="field">
                <label for="anio">Año</label>
                <InputText id="anio" v-model.trim="factura.Anio" maxlength="4" />
            </div>

            <div class="field">
                <label for="mes">Mes</label>
                <Dropdown id="mes" v-model="factura.Mes" :options="meses" 
                          optionLabel="name" optionValue="code" 
                          placeholder="Selecciona un mes" :filter="true" />
            </div>

            <div class="field">
                <label for="cuenta">Cuenta</label>
                <Dropdown id="cuenta" v-model="factura.Cuenta" :options="opcionesCuenta" placeholder="Selecciona una cuenta" />
            </div>
            
            <div class="field">
                <label for="estatus">Estatus</label>
                <Dropdown id="estatus" v-model="factura.Estatus" :options="opcionesEstatus" placeholder="Selecciona un estatus" />
            </div>
            
            <div class="field">
                <label for="monto">Monto (máx $99,999,999.99)</label>
                <InputText 
                    id="monto" 
                    v-model="montoFormateado"
                    @input="validarMontoInput"
                    @blur="formatearMontoBlur"
                    placeholder="$0.00"
                    maxlength="15"
                />
                <small v-if="montoInvalido" class="p-error">
                    El Monto debe de estar entre $0.01 y $99,999,999.99
                </small>
            </div>
            
            <div class="field">
                <label for="pdf">Archivo PDF (máx 500 KB)</label>
                
                <FileUpload 
                    ref="fileUpload"
                    v-show="!tieneArchivo"
                    name="facturaPdf" 
                    @select="onFileSelect" 
                    @clear="onFileClear"
                    :showUploadButton="false" 
                    chooseLabel="Seleccionar PDF" 
                    cancelLabel="Quitar" 
                    accept=".pdf" 
                    :maxFileSize="512000"
                    invalidFileSizeMessage="El PDF excede 500 KB">
                    <template #empty>
                        <p>Arrastra y suelta el archivo aquí (máx 500 KB)</p>
                    </template>
                </FileUpload>

                <Button 
                    v-show="tieneArchivo"
                    icon="pi pi-times" 
                    label="Quitar PDF" 
                    class="p-button-danger w-full"
                    @click="quitarArchivo"
                />

                <div v-if="factura.PDF_Base64" class="archivo-preview mt-3">
                    <div class="archivo-icono">
                        <i class="pi pi-file-pdf"></i>
                    </div>
                    <div class="archivo-info">
                        <div class="archivo-nombre">Nuevo PDF seleccionado</div>
                        <span class="badge-pendiente">Pendiente de guardar</span>
                    </div>
                </div>

                <div v-if="factura.Ruta_Archivo_PDF && !factura.PDF_Base64 && !archivoMarcadoParaEliminar" class="archivo-preview archivo-actual mt-3">
                    <div class="archivo-icono">
                        <i class="pi pi-file-pdf"></i>
                    </div>
                    <div class="archivo-info">
                        <div class="archivo-nombre">{{ obtenerNombreArchivo(factura.Ruta_Archivo_PDF) }}</div>
                        <small class="archivo-ruta">{{ factura.Ruta_Archivo_PDF }}</small>
                    </div>
                </div>

                <Message v-if="archivoMarcadoParaEliminar" severity="warn" :closable="false" class="mt-2">
                    El archivo actual se eliminará al guardar los cambios
                </Message>
            </div>

            <template #footer>
                <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="cerrarDialogo"/>
                <Button label="Guardar" icon="pi pi-check" class="p-button-text" @click="guardarFactura"/>
            </template>
        </Dialog>

        <Dialog v-model:visible="mostrarPDF" :style="{width: '90vw', height: '90vh'}" header="Visualizar Factura PDF" :modal="true" :maximizable="true">
            <iframe 
                v-if="urlPDF" 
                :src="urlPDF" 
                style="width: 100%; height: 100%; border: none;">
            </iframe>
            <p v-else class="text-center">Cargando PDF...</p>
            
            <template #footer>
                <Button label="Descargar" icon="pi pi-download" class="p-button-success" @click="descargarPDFActual" />
                <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="cerrarPDF"/>
            </template>
        </Dialog>

    </div>
</template>

<script>
import FacturacionService from '../service/FacturacionService.js';

export default {
    data() {
        return {
            lstFacturaciones: [],
            loading: false,
            mostrarDialogo: false,
            mostrarPDF: false, 
            urlPDF: null,
            pdfActual: null,
            factura: {},
            montoFormateado: '',
            montoInvalido: false,
            archivoMarcadoParaEliminar: false,
            opcionesCuenta: ['0S56008', '0F10911'],
            opcionesEstatus: ['Pendiente', 'Pagado'],
            meses: [
                { name: 'Enero', code: 1 }, { name: 'Febrero', code: 2 }, { name: 'Marzo', code: 3 },
                { name: 'Abril', code: 4 }, { name: 'Mayo', code: 5 }, { name: 'Junio', code: 6 },
                { name: 'Julio', code: 7 }, { name: 'Agosto', code: 8 }, { name: 'Septiembre', code: 9 },
                { name: 'Octubre', code: 10}, { name: 'Noviembre', code: 11}, { name: 'Diciembre', code: 12}
            ]
        };
    },

    computed: {
        tieneArchivo() {
            return this.factura.PDF_Base64 !== null || 
                   (this.factura.Ruta_Archivo_PDF !== null && !this.archivoMarcadoParaEliminar);
        }
    },

    mounted() {
        this.cargarDatos();
    },

    methods: {
        obtenerNombreMes(numeroMes) {
            const mesEncontrado = this.meses.find(m => m.code === numeroMes);
            return mesEncontrado ? mesEncontrado.name : numeroMes;
        },

        obtenerNombreArchivo(rutaCompleta) {
            if (!rutaCompleta) return '';
            const partes = rutaCompleta.split('\\');
            return partes[partes.length - 1];
        },
        
        cargarDatos() {
            this.loading = true;
            const ctlFact = new FacturacionService(); 
            ctlFact.getFacturaciones(this).then(res => {
                this.lstFacturaciones = res.map(factura => ({
                    ...factura,
                    estatusBooleano: factura.Estatus === 'Pagado'
                }));
                this.loading = false;
            }).catch(error => {
                console.error("Error al cargar facturaciones:", error);
                this.loading = false;
            });
        },

        validarMontoInput(event) {
            let valor = event.target.value;
            
            valor = valor.replace(/[^\d.]/g, '');
            
            const partes = valor.split('.');
            if (partes.length > 2) {
                valor = partes[0] + '.' + partes.slice(1).join('');
            }
            
            if (partes[0].length > 8) {
                partes[0] = partes[0].substring(0, 8);
                valor = partes.join('.');
            }
            
            if (partes.length > 1 && partes[1].length > 2) {
                partes[1] = partes[1].substring(0, 2);
                valor = partes.join('.');
            }
            
            this.montoFormateado = valor;
            event.target.value = valor;
            
            const montoNumerico = parseFloat(valor) || 0;
            
            if (montoNumerico > 99999999.99) {
                this.montoFormateado = '99999999.99';
                event.target.value = '99999999.99';
                this.montoInvalido = true;
            } else {
                this.montoInvalido = false;
            }
            
            this.factura.Monto = parseFloat(this.montoFormateado) || null;
        },
        
        formatearMontoBlur() {
            if (this.montoFormateado) {
                const monto = parseFloat(this.montoFormateado);
                if (!isNaN(monto)) {
                    this.montoFormateado = monto.toFixed(2);
                    this.factura.Monto = monto;
                } else {
                    this.montoFormateado = '';
                    this.factura.Monto = null;
                }
            }
        },
        
        abrirDialogoNuevo() {
            this.factura = {
                Anio: new Date().getFullYear(),
                Mes: null,
                Cuenta: null,
                Estatus: 'Pendiente',
                Monto: null,
                PDF_Base64: null,
                Ruta_Archivo_PDF: null
            };
            this.montoFormateado = '';
            this.montoInvalido = false;
            this.archivoMarcadoParaEliminar = false;
            
            this.$nextTick(() => {
                if (this.$refs.fileUpload) {
                    this.$refs.fileUpload.clear();
                }
            });
            
            this.mostrarDialogo = true;
        },
        
        cerrarDialogo() {
            this.mostrarDialogo = false;
            this.factura = {};
            this.montoFormateado = '';
            this.archivoMarcadoParaEliminar = false;
            
            if (this.$refs.fileUpload) {
                this.$refs.fileUpload.clear();
            }
        },
        
        editarFactura(facturaParaEditar) {
            this.factura = { 
                ...facturaParaEditar,
                PDF_Base64: null
            };
            
            this.montoFormateado = facturaParaEditar.Monto ? facturaParaEditar.Monto.toFixed(2) : '';
            this.montoInvalido = false;
            this.archivoMarcadoParaEliminar = false;
            
            this.$nextTick(() => {
                if (this.$refs.fileUpload) {
                    this.$refs.fileUpload.clear();
                }
            });
            
            this.mostrarDialogo = true;
        },

        confirmarEliminar(facturaParaEliminar) {
            this.$confirm.require({
                message: '¿Estás seguro de que quieres eliminar este registro?',
                header: 'Confirmación de Eliminación',
                icon: 'pi pi-info-circle',
                acceptClass: 'p-button-danger',
                accept: () => {
                    this.loading = true;
                    const ctlFact = new FacturacionService(); 
                    ctlFact.deleteFactura(this, facturaParaEliminar.Id_Facturacion).then(() => {
                        this.$toast.add({severity:'success', summary: 'Confirmado', detail: 'Registro eliminado', life: 3000});
                        this.cargarDatos();
                    }).catch(() => {
                        this.loading = false;
                    });
                }
            });
        },

        guardarFactura() {
            if (!this.factura.Anio || this.factura.Anio.toString().length !== 4) {
                this.$toast.add({severity:'error', summary: 'Error de Validación', detail: 'El año debe ser un número de 4 dígitos.', life: 3000});
                return;
            }
            if (!this.factura.Mes) {
                this.$toast.add({severity:'error', summary: 'Error de Validación', detail: 'Debe seleccionar un mes.', life: 3000});
                return; 
            }
            if (!this.factura.Cuenta) {
                this.$toast.add({severity:'error', summary: 'Error de Validación', detail: 'Debe seleccionar una cuenta.', life: 3000});
                return; 
            }
            if (!this.factura.Estatus) {
                this.$toast.add({severity:'error', summary: 'Error de Validación', detail: 'Debe seleccionar un estatus.', life: 3000});
                return; 
            }
            if (!this.factura.Monto || this.factura.Monto <= 0) {
                this.$toast.add({severity:'error', summary: 'Error de Validación', detail: 'El monto debe ser un valor mayor a 0.', life: 3000});
                return;
            }
            if (this.factura.Monto > 99999999.99) {
                this.$toast.add({severity:'error', summary: 'Error de Validación', detail: 'El monto es demasiado grande.', life: 3000});
                return;
            }

            this.loading = true;
            const ctlFact = new FacturacionService();
            
            const facturaParaEnviar = {
                Anio: parseInt(this.factura.Anio),
                Mes: this.factura.Mes,
                Cuenta: this.factura.Cuenta,
                Estatus: this.factura.Estatus,
                Monto: this.factura.Monto,
                PDF_Base64: this.factura.PDF_Base64 || null
            };
            
            if(this.factura.Id_Facturacion) {
                facturaParaEnviar.Id_Facturacion = this.factura.Id_Facturacion;
                
                if (this.archivoMarcadoParaEliminar) {
                    facturaParaEnviar.Ruta_Archivo_PDF = null;
                } else if (!this.factura.PDF_Base64) {
                    facturaParaEnviar.Ruta_Archivo_PDF = this.factura.Ruta_Archivo_PDF || null;
                }
            }
            
            let promise;
            if (this.factura.Id_Facturacion) {
                promise = ctlFact.updateFactura(this, facturaParaEnviar); 
            } else {
                promise = ctlFact.addFactura(this, facturaParaEnviar);
            }

            promise.then(() => {
                const detail = this.factura.Id_Facturacion ? 'Registro actualizado' : 'Registro guardado';
                this.$toast.add({severity:'success', summary: 'Éxito', detail: detail, life: 3000});
                this.cerrarDialogo();
                this.cargarDatos(); 
            }).catch((error) => {
                console.error("Error completo:", error);
                this.loading = false;
            });
        },

        quitarArchivo() {
            if (this.factura.PDF_Base64) {
                this.factura.PDF_Base64 = null;
                
                if (this.$refs.fileUpload) {
                    this.$refs.fileUpload.clear();
                }

                this.$toast.add({
                    severity: 'info',
                    summary: 'Archivo removido',
                    detail: 'El archivo nuevo ha sido removido',
                    life: 2000
                });
            }
            else if (this.factura.Ruta_Archivo_PDF) {
                this.$confirm.require({
                    message: '¿Estás seguro de eliminar el archivo PDF actual?',
                    header: 'Confirmar eliminación',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Sí, eliminar',
                    rejectLabel: 'Cancelar',
                    acceptClass: 'p-button-danger',
                    accept: () => {
                        this.archivoMarcadoParaEliminar = true;
                        this.factura.Ruta_Archivo_PDF = null;
                        
                        this.$toast.add({
                            severity: 'warn',
                            summary: 'Archivo marcado para eliminar',
                            detail: 'Se eliminará al guardar los cambios',
                            life: 3000
                        });
                    }
                });
            }
        },

        onFileSelect(event) {
            const file = event.files ? event.files[0] : (event.originalEvent?.dataTransfer?.files?.[0] || null);
            
            if (!file) {
                return;
            }

            if (file.size > 512000) {
                this.$toast.add({
                    severity:'error', 
                    summary: 'Archivo muy grande', 
                    detail: 'El PDF no debe exceder los 500 KB. Por favor, comprime el archivo antes de subirlo.', 
                    life: 5000
                });

                if (this.$refs.fileUpload) {
                    this.$refs.fileUpload.clear();
                }
                return;
            }
            
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result.split(',')[1];
                this.factura.PDF_Base64 = base64;

                this.$toast.add({
                    severity:'info', 
                    summary: 'Archivo cargado', 
                    detail: `${file.name} (${(file.size / 1024).toFixed(2)} KB)`, 
                    life: 2000
                });
            };    
            reader.onerror = () => {
                this.$toast.add({
                    severity:'error', 
                    summary: 'Error', 
                    detail: 'No se pudo leer el archivo PDF', 
                    life: 3000
                });
            };
            reader.readAsDataURL(file);
        },
        
        onFileClear() {
            this.factura.PDF_Base64 = null;
        },
        
        verPDF(factura) {
            if (!factura.Ruta_Archivo_PDF) {
                this.$toast.add({
                    severity:'warn', 
                    summary: 'Sin archivo', 
                    detail: 'Esta factura no tiene PDF adjunto', 
                    life: 3000
                });
                return;
            }

            this.pdfActual = factura;
            
            const baseUrl = this.$store.getters.getUrlServicios.replace('/Servicios/', '');
            this.urlPDF = `${baseUrl}/Uploads/Facturaciones/${factura.Ruta_Archivo_PDF}`;
            
            this.mostrarPDF = true;
        },

        descargarPDF(factura) {
            if (!factura.Ruta_Archivo_PDF) {
                this.$toast.add({
                    severity:'warn', 
                    summary: 'Sin archivo', 
                    detail: 'Esta factura no tiene PDF adjunto', 
                    life: 3000
                });
                return;
            }

            const baseUrl = this.$store.getters.getUrlServicios.replace('/Servicios/', '');
            const pdfUrl = `${baseUrl}/Uploads/Facturaciones/${factura.Ruta_Archivo_PDF}`;
            
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `Factura_${factura.Anio}_${this.obtenerNombreMes(factura.Mes)}_${factura.Cuenta}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            this.$toast.add({
                severity:'success', 
                summary: 'Descargando', 
                detail: 'El PDF se está descargando', 
                life: 2000
            });
        },

        descargarPDFActual() {
            if (this.pdfActual) {
                this.descargarPDF(this.pdfActual);
            }
        },

        cerrarPDF() {
            this.mostrarPDF = false;
            this.urlPDF = null;
            this.pdfActual = null;
        },
        
        formatCurrency(value) {
            if (value === null || value === undefined) return '$0.00';
            return parseFloat(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
        },

        cambiarEstatus(factura) {
            const nuevoEstatus = factura.estatusBooleano ? 'Pagado' : 'Pendiente';
            
            this.loading = true;
            
            let facturaParaActualizar = {
                Id_Facturacion: factura.Id_Facturacion,
                Estatus: nuevoEstatus
            };

            const ctlFact = new FacturacionService();
            
            ctlFact.actualizarEstatus(this, facturaParaActualizar)
                .then(() => {
                    const mensaje = nuevoEstatus === 'Pagado' ? 'Factura marcada como pagada' : 'Factura marcada como pendiente';
                    this.$toast.add({severity:'success', summary: 'Éxito', detail: mensaje, life: 3000});
                    this.cargarDatos(); 
                })
                .catch((error) => {
                    console.error("Error al actualizar estatus:", error);
                    factura.estatusBooleano = !factura.estatusBooleano;
                    this.loading = false;
                });
        }
    } 
}
</script>

<style scoped>
.estatus-badge {
    padding: .25em .5rem;
    border-radius: 3px; 
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
    text-transform: uppercase;
}
.estatus-pagado {
    background-color: var(--green-100);
    color: var(--green-700);
}
.estatus-pendiente {
    background-color: var(--red-100);
    color: var(--red-700);
}
.text-muted {
    color: #6c757d;
    font-style: italic;
    font-size: 0.875rem;
}

.archivo-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 8px;
}

.archivo-actual {
    border-color: #10b981;
    background: #ecfdf5;
}

.archivo-icono {
    width: 40px;
    height: 40px;
    background: #fee2e2;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.archivo-icono i {
    font-size: 20px;
    color: #dc2626;
}

.archivo-info {
    flex: 1;
    min-width: 0;
}

.archivo-nombre {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.archivo-ruta {
    font-size: 0.75rem;
    color: #10b981;
    display: block;
    margin-top: 4px;
}

.badge-pendiente {
    display: inline-block;
    padding: 2px 8px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    margin-top: 4px;
}
</style>
